"use client";

import Link from "next/link";
import type { MouseEventHandler, ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";

/**
 * Erkennt den aktiven Hauptnav-Bereich (backlog.md -> "Aktueller Nav-Bereich muss in
 * der Hauptnavigation sichtbar sein", Landingpage-Feedback 2026-08-17 Punkt 14).
 * Bewusst ZWEI getrennte Varianten statt einer gemeinsamen Komponente: `usePathname()`
 * allein zwingt eine Seite nicht in dynamisches Rendering, `useSearchParams()` schon
 * (Next.js baut die Seite sonst nicht mehr statisch, siehe /akademie, /akademie/[slug],
 * /themen - alle drei aktuell bewusst statisch). Die reinen Pfad-Links (6 von 8) nutzen
 * daher nur usePathname, nur die beiden /dashboard-Tab-Links unten brauchen
 * useSearchParams und werden dafuer gezielt in eine eigene <Suspense> gepackt.
 *
 * Die mobilen Varianten nehmen zusaetzlich `onClick` entgegen und reichen es an <Link>
 * durch - noetig, damit `<SheetClose asChild>` (site-header.tsx, schliesst das Menue bei
 * Klick) sein injiziertes onClick ans eigentliche DOM-Element weiterreichen kann.
 */
function isPathActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

const MOBILE_LINK_CLASS = "rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted";

/** Desktop-Textlink in der Hauptnav fuer reine Pfad-Ziele (kein `?tab=`). */
export function HeaderNavLink({
  href,
  className,
  children,
}: {
  href: string;
  className?: string;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const active = isPathActive(pathname, href);
  return (
    <Button variant="ghost" size="sm" asChild className={className}>
      <Link href={href} className={active ? "text-gold-text font-semibold" : undefined} aria-current={active ? "page" : undefined}>
        {children}
      </Link>
    </Button>
  );
}

/** Link im mobilen Hamburger-Sheet fuer reine Pfad-Ziele - gleiche Aktiv-Erkennung wie
 * HeaderNavLink, andere Optik (volle Zeile statt Button). */
export function MobileNavLink({
  href,
  onClick,
  children,
}: {
  href: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const active = isPathActive(pathname, href);
  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`${MOBILE_LINK_CLASS} ${active ? "text-gold-text font-semibold" : "text-ink"}`}
    >
      {children}
    </Link>
  );
}

/** `/dashboard?tab=depot`/`/dashboard?tab=watchlist` - einzige beiden Nav-Ziele mit
 * Query-Param-Zustand, deshalb eigene Variante mit useSearchParams (Aufrufer muss dies
 * in <Suspense> wrappen). Spiegelt exakt app/dashboard/page.tsx' eigene
 * `defaultTab`-Fallback-Regel (kein/unbekannter Tab-Wert -> "watchlist"), sonst waere
 * "Watchlist" beim direkten Aufruf von `/dashboard` ohne Query faelschlich nie aktiv. */
function useIsDashboardTabActive(tab: "depot" | "watchlist"): boolean {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  if (pathname !== "/dashboard") return false;
  const currentTab = searchParams.get("tab");
  const effectiveTab = currentTab === "depot" || currentTab === "benachrichtigungen" ? currentTab : "watchlist";
  return effectiveTab === tab;
}

export function HeaderDashboardTabLink({
  tab,
  className,
  children,
}: {
  tab: "depot" | "watchlist";
  className?: string;
  children: ReactNode;
}) {
  const active = useIsDashboardTabActive(tab);
  return (
    <Button variant="ghost" size="sm" asChild className={className}>
      <Link
        href={`/dashboard?tab=${tab}`}
        className={active ? "text-gold-text font-semibold" : undefined}
        aria-current={active ? "page" : undefined}
      >
        {children}
      </Link>
    </Button>
  );
}

export function MobileDashboardTabLink({
  tab,
  onClick,
  children,
}: {
  tab: "depot" | "watchlist";
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  children: ReactNode;
}) {
  const active = useIsDashboardTabActive(tab);
  return (
    <Link
      href={`/dashboard?tab=${tab}`}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`${MOBILE_LINK_CLASS} ${active ? "text-gold-text font-semibold" : "text-ink"}`}
    >
      {children}
    </Link>
  );
}
